import { AppAlert, AppBadge, AppCard, AppLoadingSpinner, AppTooltip } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@9b3kuq5phwuk4txw/kiban-react-ui/primitives'
import { useNavigate } from '@tanstack/react-router'
import type { SharableOpportunity } from 'backend/types'
import Autoplay from 'embla-carousel-autoplay'
import { useRef } from 'react'
import { useSharableOpportunities } from '@/shared-hooks/useSharableOpportunities'
import { useAuthStore } from '@/stores/auth'

/**
 * 公開オポチュニティ事例をカルーセル表示します。
 *
 * - キャプテン以外は非表示（API呼び出しもスキップ）
 * - 8秒間隔で自動スライド（ホバー時は停止）
 * - 公開オポのみを表示（is_release: true）
 */
export function SharableOpportunityCarousel() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const isCaptain = user?.isCaptain ?? false

  const plugin = useRef(
    Autoplay({
      delay: 8000,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    }),
  )

  const { data, isLoading, error } = useSharableOpportunities({
    query: { onlyRelease: true },
    enabled: isCaptain,
  })

  if (!isCaptain) {
    return null
  }

  const opportunities: SharableOpportunity[] = data?.opportunities ?? []

  const renderContent = () => {
    if (isLoading) {
      return (
        <AppCard className='h-75 max-w-175 justify-center'>
          <AppLoadingSpinner text='読み込み中...' size='default' />
        </AppCard>
      )
    }

    if (error) {
      return (
        <AppAlert type='error'>
          公開オポチュニティの取得に失敗しました。
          {error.message && <div className='mt-1 text-xs opacity-80'>{error.message}</div>}
        </AppAlert>
      )
    }

    if (opportunities.length === 0) {
      return <AppAlert type='info'>現在、公開されているオポチュニティ事例はありません。</AppAlert>
    }

    return (
      <Carousel
        opts={{
          loop: true,
          align: 'start',
        }}
        plugins={[plugin.current]}
        className='w-full max-w-175 shadow-md'
      >
        <CarouselContent>
          {opportunities.map((opportunity) => (
            <CarouselItem key={opportunity.id}>
              <AppTooltip content='クリックして詳細を表示する' side='bottom' delayDuration={300}>
                <AppCard
                  title={`公開オポ事例 その${opportunity.id}`}
                  className='cursor-pointer h-75 overflow-auto'
                  onClick={() => {
                    navigate({
                      to: '/opportunities/public/$id/properties',
                      params: { id: String(opportunity.id) },
                    })
                  }}
                >
                  <div className='space-y-3'>
                    <div className='flex space-x-2'>
                      {opportunity.shift_service_name && (
                        <AppBadge color='orange'>{opportunity.shift_service_name}</AppBadge>
                      )}
                      {opportunity.shift_service_categories_name && (
                        <AppBadge color='green'>{opportunity.shift_service_categories_name}</AppBadge>
                      )}
                    </div>

                    <table className='text-sm'>
                      <thead>
                        <tr>
                          <th className='text-left py-2 px-2 font-semibold w-24'>項目</th>
                          <th className='text-left py-2 px-2 font-semibold'>内容</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className='border-t'>
                          <th className='text-left py-2 px-2 font-semibold align-top'>顧客課題</th>
                          <td className='py-2 px-2'>{opportunity.customer_issue}</td>
                        </tr>
                        <tr className='border-t'>
                          <th className='text-left py-2 px-2 font-semibold align-top'>提案内容</th>
                          <td className='py-2 px-2'>{opportunity.proposal_summary}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </AppCard>
              </AppTooltip>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className='hover:bg-accent border border-border' />
        <CarouselNext className='hover:bg-accent border border-border' />
      </Carousel>
    )
  }

  return (
    <div className='space-y-4'>
      <h3 className='text-lg font-semibold'>公開オポ事例</h3>
      <div className='flex justify-center'>{renderContent()}</div>
    </div>
  )
}
